<template>
    <div class="col-span-12 mt-8">
        <div class="intro-y flex items-center h-15">
            <h2 class="text-lg font-medium truncate mr-5">
                Revenue
            </h2>
        </div>
        <div class="grid grid-cols-10 gap-5 mt-5">
            <div class="col-span-10 sm:col-span-5 md:col-span-4 lg:col-span-3 xl:col-span-2 intro-y">
                <div class="report-box zoom-in">
                    <div class="box px-5 py-3">
                        <div class="flex">
                            <fa-icon :icon="['fas', 'chart-line']" class="report-box__icon text-theme-10" /> 
                            <div class="ml-auto">
                                <div v-if="revenue.today_diff > 0" class="report-box__indicator bg-theme-9 tooltip cursor-pointer"
                                    :title="revenue.today_diff + '% Higher than Yesterday'"> 
                                    {{ revenue.today_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-up']" class="w-4 h-4" />&nbsp;
                                </div>
                                <div v-if="revenue.today_diff < 0" class="report-box__indicator bg-theme-6 tooltip cursor-pointer"
                                    :title="revenue.today_diff + '% Lower than Yesterday'"> 
                                    {{ revenue.today_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-down']" class="w-4 h-4" />&nbsp;
                                </div>
                            </div>
                        </div>
                        <div class="text-xl font-bold leading-8 mt-2">
                            ${{ revenue.today | withComma }}
                            <span v-if="isLoading || revenue.today == null">0</span>
                        </div>
                        <div class="text-base text-gray-600 mt-1">Today</div>
                    </div>
                </div>
            </div>
            <div class="col-span-10 sm:col-span-5 md:col-span-4 lg:col-span-3 xl:col-span-2 intro-y">
                <div class="report-box zoom-in">
                    <div class="box px-5 py-3">
                        <div class="flex">
                            <fa-icon :icon="['fas', 'chart-line']" class="report-box__icon text-theme-11" />
                            <div class="ml-auto">
                                <div v-if="revenue.yesterday_diff > 0" class="report-box__indicator bg-theme-9 tooltip cursor-pointer" 
                                    :title="revenue.yesterday_diff + '% Higher than two days ago'"> 
                                    {{ revenue.yesterday_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-up']" class="w-4 h-4" />&nbsp;
                                </div>
                                <div v-if="revenue.yesterday_diff < 0" class="report-box__indicator bg-theme-6 tooltip cursor-pointer" 
                                    :title="revenue.yesterday_diff + '% Lower than two days ago'"> 
                                    {{ revenue.yesterday_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-down']" class="w-4 h-4" />&nbsp;
                                </div>
                            </div>
                        </div>
                        <div class="text-xl font-bold leading-8 mt-2">
                            ${{ revenue.yesterday | withComma }}
                            <span v-if="isLoading || revenue.yesterday == null">0</span>
                        </div>
                        <div class="text-base text-gray-600 mt-1">Yesterday</div>
                    </div>
                </div>
            </div>
            <div class="col-span-10 sm:col-span-5 md:col-span-4 lg:col-span-3 xl:col-span-2 intro-y">
                <div class="report-box zoom-in">
                    <div class="box px-5 py-3">
                        <div class="flex">
                            <fa-icon :icon="['fas', 'chart-line']" class="report-box__icon text-theme-12" />
                            <div class="ml-auto">
                                <div v-if="revenue.seven_days_diff > 0" class="report-box__indicator bg-theme-9 tooltip cursor-pointer" 
                                    :title="revenue.seven_days_diff + '% Higher than two weeks ago'"> 
                                    {{ revenue.seven_days_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-up']" class="w-4 h-4" />&nbsp;
                                </div>
                                <div v-if="revenue.seven_days_diff < 0" class="report-box__indicator bg-theme-6 tooltip cursor-pointer" 
                                    :title="revenue.seven_days_diff + '% Lower than two weeks ago'"> 
                                    {{ revenue.seven_days_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-down']" class="w-4 h-4" />&nbsp;
                                </div>
                            </div>
                        </div>
                        <div class="text-xl font-bold leading-8 mt-2">
                            ${{ revenue.seven_days | withComma }}
                            <span v-if="isLoading || revenue.seven_days == null">0</span>
                        </div>
                        <div class="text-base text-gray-600 mt-1">7 days</div>
                    </div>
                </div>
            </div>
            <div class="col-span-10 sm:col-span-5 md:col-span-4 lg:col-span-3 xl:col-span-2 intro-y">
                <div class="report-box zoom-in">
                    <div class="box px-5 py-3">
                        <div class="flex">
                            <fa-icon :icon="['fas', 'chart-line']" class="report-box__icon text-theme-11" />
                            <div class="ml-auto">
                                <div v-if="revenue.current_month_diff > 0" class="report-box__indicator bg-theme-9 tooltip cursor-pointer" 
                                    :title="revenue.current_month_diff + '% Higher than month ago'"> 
                                    {{ revenue.current_month_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-up']" class="w-4 h-4" />&nbsp;
                                </div>
                                <div v-if="revenue.current_month_diff < 0" class="report-box__indicator bg-theme-6 tooltip cursor-pointer" 
                                    :title="revenue.current_month_diff + '% Lower than month ago'"> 
                                    {{ revenue.current_month_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-down']" class="w-4 h-4" />&nbsp;
                                </div>
                            </div>
                        </div>
                        <div class="text-xl font-bold leading-8 mt-1">
                            ${{ revenue.current_month | withComma }}
                            <span v-if="isLoading || revenue.current_month == null">0</span>
                        </div>
                        <div class="text-base text-gray-600 mt-1">This Month</div>
                    </div>
                </div>
            </div>
            <div class="col-span-10 sm:col-span-5 md:col-span-4 lg:col-span-3 xl:col-span-2 intro-y">
                <div class="report-box zoom-in">
                    <div class="box px-5 py-3">
                        <div class="flex">
                            <fa-icon :icon="['fas', 'chart-line']" class="report-box__icon text-theme-9" />
                            <div class="ml-auto">
                                <div v-if="revenue.last_month_diff > 0" class="report-box__indicator bg-theme-9 tooltip cursor-pointer" 
                                    :title="revenue.last_month_diff + '% Higher than two months ago'"> 
                                    {{ revenue.last_month_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-up']" class="w-4 h-4" />&nbsp;
                                </div>
                                <div v-if="revenue.last_month_diff < 0" class="report-box__indicator bg-theme-6 tooltip cursor-pointer" 
                                    :title="revenue.last_month_diff + '% Lower than two months ago'"> 
                                    {{ revenue.last_month_diff }}% &nbsp;<fa-icon :icon="['fas', 'chevron-down']" class="w-4 h-4" />&nbsp;
                                </div>
                            </div>
                        </div>
                        <div class="text-xl font-bold leading-8 mt-1">
                            ${{ revenue.last_month | withComma }}
                            <span v-if="isLoading || revenue.last_month == null">0</span>
                        </div>
                        <div class="text-base text-gray-600 mt-1">Last Month</div>
                    </div>
                </div>
            </div>            
        </div>
    </div>
</template>

<script>

export default {
    props: {
        revenue: {
            required: false,
            type: Object | Array
        },
        isLoading: {
            required: true,
            type: Boolean
        },
    },
    data() {
        return {
            
        }
    },
    created() {
    },
    methods: {
    },
    watch: {
        revenue: {
            handler: function() {
                this.$feather.replace()
            },
            deep: true
        }
    }
}
</script>
<style scoped>
.info-box-icon p {
    margin-top: 16px;
    font-size: 17px;
}
.background1{
    background-image: linear-gradient(to right,#2c3e53,#263544 );
    border-bottom-left-radius: .5rem;
    border-top-left-radius: .5rem;
}
.background2{
    background-image: linear-gradient(to right,#263544,#1E2A36);
}
.background3{
    background-image: linear-gradient(to right,#1E2A36,#131B22);
}
.background4{
    background-image: linear-gradient(to right,#131B22,#0F161B);
}
.background5{
    background-image: linear-gradient(to right,#080B0E ,#000000 );
    border-bottom-right-radius: .5rem;
    border-top-right-radius: .5rem;
}
.background-item{
    background-color:transparent;
    color: whitesmoke;
    box-shadow: 0px 3px 20px #0000000b;
    --bg-opacity: 1;
    background-color: #fff;
    background-color: rgba(255, 255, 255, var(--bg-opacity));
    /* border-radius: 0.375rem; */
    position: relative;
}
.report-box-indicator{
    padding-top: 3px;
    padding-bottom: 3px;
    display: flex;
    border-radius: 9999px;
    --text-opacity: 1;
    color: #fff;
    color: rgba(255, 255, 255, var(--text-opacity));
    font-size: 0.75rem;
    padding-left: 0.5rem;
    padding-right: 0.25rem;
    align-items: center;
    font-weight: 500;
}
.sm-text{
    color: #240000;
}
@media (max-width: 639px) { 
 .background1{
    border-bottom-left-radius: 0rem;
    border-top-left-radius: .5rem;
    border-top-right-radius: .5rem;
    }
.background5{
    border-bottom-right-radius: .5rem;
    border-top-right-radius: 0rem;
    border-bottom-left-radius: .5rem;
    }   
}
</style>